import { createRoute, redirect } from '@tanstack/react-router';
import { Route as appRoute } from '../../app.js';
import { COMMISSION_PROJECTION_DISCLAIMER } from '@admitly/shared';
import { useAuthStore } from '../../../lib/auth.js';
import { canSeeCommissionEarnings } from '../../../lib/capabilities.js';
import { centsToAudDisplay } from '../../../lib/money.js';
import { AdminPageHeader } from '../../../components/admin/AdminPageHeader.js';
import {
  useCommissionDeals,
  useCommissionEarnings,
  type EarningsPaymentRoute,
} from '../../../hooks/useCommissionEarnings.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'commission/earnings',
  // Gates on the view_commission CAPABILITY (Brief BUILD-COMMISSION-VIEW): holders
  // are super_admin/admin/owner/accounts; consultant/offshore are redirected. The
  // server's requireCapability + the DB commission-view wall (0024) are the real
  // gates — this only keeps a walled user off a screen they can't be served.
  beforeLoad: () => {
    if (!canSeeCommissionEarnings(useAuthStore.getState().user)) {
      throw redirect({ to: '/app' });
    }
  },
  component: CommissionEarningsPage,
});

function routeLabel(route: EarningsPaymentRoute): string {
  if (route === 'via_aussizz') return 'Via Aussizz';
  if (route === 'direct') return 'Direct';
  return 'Unspecified';
}

/**
 * The projection caveat block — shown at the top of the earnings screen, above
 * every commission figure. ONE notice (Brief COPY collapsed the three
 * overlapping notices into this): every commission dollar figure is a FORECAST
 * off the quoted fee, not money received. It does not change any figure — copy
 * only. The earlier "the formula itself is provisional / pending confirmation"
 * caveat was removed: the formula is confirmed.
 */
function ProjectionDisclaimer() {
  return (
    <div className="mb-4 rounded-lg border border-line bg-cream/60 px-4 py-3 text-sm text-muted">
      <p className="font-medium text-ink">{COMMISSION_PROJECTION_DISCLAIMER}</p>
    </div>
  );
}

/**
 * Read-only commission earnings (Brief BUILD-COMMISSION-VIEW). The per-route
 * breakdown and per-deal list lead; the headline total is a projection off the
 * quoted fees, carried by the single projection notice above it. No writes.
 */
export function CommissionEarningsPage() {
  const earnings = useCommissionEarnings();
  const deals = useCommissionDeals();

  const summary = earnings.data;
  const dealRows = deals.data?.deals ?? [];
  const scopeLabel = summary?.scope === 'own_branch' ? 'Your branch' : 'All branches';

  return (
    <div>
      <AdminPageHeader
        title="Projected commission"
        description="Projected commission across issued quotes, estimated from the quoted fees."
      />

      <ProjectionDisclaimer />

      {earnings.isLoading && <p className="px-1 py-6 text-sm text-muted">Loading earnings…</p>}
      {earnings.error && (
        <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
          Couldn&apos;t load earnings: {(earnings.error as Error).message}{' '}
          <button className="underline" onClick={() => earnings.refetch()}>
            Retry
          </button>
        </div>
      )}

      {summary && (
        <>
          {/* Totals header — total is a PROJECTION (see the block above). */}
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <div className="surface p-4">
              <p className="text-xs uppercase tracking-wide text-muted">
                Total projected commission
              </p>
              <p className="mt-1 text-2xl font-semibold text-ink">
                {centsToAudDisplay(summary.totalIndicativeCents)}
              </p>
            </div>
            <div className="surface p-4">
              <p className="text-xs uppercase tracking-wide text-muted">Deals</p>
              <p className="mt-1 text-2xl font-semibold text-ink">{summary.dealCount}</p>
            </div>
            <div className="surface p-4">
              <p className="text-xs uppercase tracking-wide text-muted">Scope</p>
              <p className="mt-1 text-2xl font-semibold text-ink">{scopeLabel}</p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3 items-start">
            <BreakdownTable
              heading="By payment route"
              rows={summary.byPaymentRoute.map((r) => ({
                key: r.paymentRoute,
                label: routeLabel(r.paymentRoute),
                totalIndicativeCents: r.totalIndicativeCents,
                dealCount: r.dealCount,
              }))}
            />
            <BreakdownTable
              heading="By college"
              rows={summary.byCollege.map((c) => ({
                key: c.collegeName,
                label: c.collegeName,
                totalIndicativeCents: c.totalIndicativeCents,
                dealCount: c.dealCount,
              }))}
            />
            <BreakdownTable
              heading="By branch"
              rows={summary.byBranch.map((b) => ({
                key: b.branchId ?? b.branchName,
                label: b.branchName,
                totalIndicativeCents: b.totalIndicativeCents,
                dealCount: b.dealCount,
              }))}
            />
          </div>
        </>
      )}

      {/* Per-deal list. */}
      <div className="mt-6 surface p-4">
        <h2 className="mb-3 text-sm font-semibold text-ink">Deals</h2>
        {deals.isLoading && <p className="px-1 py-6 text-sm text-muted">Loading deals…</p>}
        {deals.error && (
          <p className="px-1 py-6 text-sm text-danger">
            Couldn&apos;t load deals: {(deals.error as Error).message}
          </p>
        )}
        {!deals.isLoading && !deals.error && dealRows.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No commission deals yet.</p>
        )}
        {dealRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted">
                  <th className="py-2 pr-3">Quote</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Branch</th>
                  <th className="py-2 pr-3">College / course</th>
                  <th className="py-2 pr-3">Route</th>
                  <th className="py-2 pr-3 text-right">Net fee</th>
                  <th className="py-2 pr-3 text-right">Projected commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {dealRows.map((d, i) => (
                  <tr key={`${d.quoteId}-${i}`}>
                    <td className="py-2 pr-3 text-ink">{d.quoteNumber}</td>
                    <td className="py-2 pr-3 text-muted">{d.quoteDate}</td>
                    <td className="py-2 pr-3 text-ink">{d.clientName}</td>
                    <td className="py-2 pr-3 text-muted">{d.branchName ?? 'Unassigned'}</td>
                    <td className="py-2 pr-3 text-ink">
                      <span className="block">{d.collegeName}</span>
                      <span className="block text-xs text-muted">{d.courseName}</span>
                    </td>
                    <td className="py-2 pr-3 text-muted">{routeLabel(d.paymentRoute)}</td>
                    <td className="py-2 pr-3 text-right text-muted">
                      {centsToAudDisplay(d.netFeeCents)}
                    </td>
                    <td className="py-2 pr-3 text-right text-ink">
                      {centsToAudDisplay(d.indicativeCommissionCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BreakdownTable({
  heading,
  rows,
}: {
  heading: string;
  rows: { key: string; label: string; totalIndicativeCents: number; dealCount: number }[];
}) {
  return (
    <div className="surface p-4">
      <h2 className="mb-3 text-sm font-semibold text-ink">{heading}</h2>
      {rows.length === 0 ? (
        <p className="px-1 py-4 text-sm text-muted">Nothing yet.</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-ink">{r.label}</span>
              <span className="text-xs text-muted">{r.dealCount}</span>
              <span className="text-sm text-ink">{centsToAudDisplay(r.totalIndicativeCents)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
